import classes from './styles.module.scss';
import { memo, useEffect, useState } from 'react';
import { Box, Card, CardContent, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import Page1 from './component/Page1';
import Page2 from './component/Page2';
import Page3 from './component/Page3';
import { DetailReport, ETYPE_PAGE_REPORT } from 'interfaces/report';
import PDFDocument, { ItemPagePdf } from './component/PDFDocument';
import { pdf } from '@react-pdf/renderer';
import Highcharts from 'highcharts';
import { CloseIcon } from 'assets';
import moment from 'moment';
import exporting from 'highcharts/modules/exporting';
import exportData from 'highcharts/modules/export-data'; // nếu bạn dùng export dữ liệu
import accessibility from 'highcharts/modules/accessibility';
import { useDispatch } from 'react-redux';
import { setIsLoadingReducer } from 'redux/reducers/Status/actionTypes';
exporting(Highcharts);
exportData(Highcharts);
accessibility(Highcharts);

interface ModalReportProps {
  isOpen: boolean;
  onClose: () => void;
  detailReport: DetailReport;
}
export interface DataReportAll {
  title: string;
  options: Highcharts.Options;
}

const percentageMapWidth = {
  1: "550",
  2: "220",
  3: "220",
  4: "220",
  5: "220",
};
const percentageMapHeight = {
  1: "300",
  2: "150",
  3: "150",
  4: "150",
  5: "150",
};

// Hàm chuyển SVG sang PNG
const svgToPng = (svg: string, width: number, height: number): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Không thể tạo canvas context'));
      return;
    }

    canvas.width = width;
    canvas.height = height;

    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    img.onload = () => {
      ctx.drawImage(img, 0, 0, width, height);
      const pngDataUrl = canvas.toDataURL('image/png');
      URL.revokeObjectURL(url);
      resolve(pngDataUrl);
    };

    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };

    img.src = url;
  });
};

const ModalReport = memo(({ isOpen, onClose, detailReport }: ModalReportProps) => {

  const dispatch = useDispatch();

  const [page, setPage] = useState<number>(1);

  const [pageComponent, setPageComponent] = useState<React.ReactNode>(null);

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const _onClose = () => {
    setPage(1);
    onClose();
  };

  useEffect(() => {
    const fetchPage = async () => {
      const detailPage = detailReport?.pages?.find((it) => it.page === page);
      if (detailPage) {
        switch (detailPage.type) {
          case ETYPE_PAGE_REPORT.COVER_PAGE:
            setPageComponent(<Page1 detailReport={detailReport} />);
            break;
          case ETYPE_PAGE_REPORT.INTRODUCE_PAGE:
            setPageComponent(<Page2 />);
            break;
          case ETYPE_PAGE_REPORT.DATA_PAGE:
            setPageComponent(<Page3 dataReportPage={detailPage} />);
            break;
          default:
            setPageComponent(<></>);
        }
      }
    };
    fetchPage();
  }, [detailReport, page]);

  const nextPage = () => {
    const _page: number = page + 1;
    setPage(_page);
  };

  const prePage = () => {
    const _page: number = page - 1;
    setPage(_page);
  };

  const waitForChartRender = (chart: Highcharts.Chart) =>
  new Promise<void>((resolve) => {
    const onRender = () => {
      chart?.renderer?.box?.addEventListener?.("render", () => {
        resolve();
      });
      // Nếu event không có, fallback sau 200ms
      setTimeout(resolve, 200);
    };
    onRender();
  });


  const exportPDF = async () => {
    dispatch(setIsLoadingReducer(true))
    await Promise.all([
      generatePDFBlob()
    ]).then((res) => {
      const blob = res[0];
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `export_${moment().format(
        "YYYY-MM-DD_HH_mm_ss"
      )}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    }).catch((res) => {console.log(`Error: ${res}`)})
    .finally(() => {dispatch(setIsLoadingReducer(false))})
    // const blob = await generatePDFBlob();
    // const url = URL.createObjectURL(blob);
    // const link = document.createElement('a');
    // link.href = url;
    // link.download = `export_${moment().format(
    //   "YYYY-MM-DD_HH_mm_ss"
    // )}.pdf`;
    // link.click();
    // URL.revokeObjectURL(url);
  }

  const generatePDFBlob = async () => {
    const items: ItemPagePdf[] = [];

    for (const [sectionIndex, section] of detailReport.pages.entries()) {
      const chartImages: string[] = [];
      for (const [chartIndex, chartOptions] of section?.charts?.entries() ?? []) {
        // Tạo container tạm thời
        const container = document.createElement('div');
        container.style.position = 'absolute';
        container.style.left = '-9999px'; // Đặt ngoài màn hình
        document.body.appendChild(container);

        try {
          // Tạo biểu đồ Highcharts
          const chart = Highcharts.chart(container, {
            ...chartOptions,
            chart: {
              ...chartOptions.chart,
              width: percentageMapWidth[section?.dataChart?.length],
              height: percentageMapHeight[section?.dataChart?.length],
            },
          });
          await waitForChartRender(chart);
          // Lấy SVG từ biểu đồ
          const svg = chart.getSVG();
          const chartImage = await svgToPng(svg, percentageMapWidth[section?.dataChart?.length], percentageMapHeight[section?.dataChart?.length]);
          chartImages.push(chartImage);
        } catch (err) {
          console.error(`Error chart ${sectionIndex}-${chartIndex}:`, err);
        } finally {
          // Hủy biểu đồ và xóa container
          container.remove();
        }
      }

      items.push({
        chartImages: chartImages,
        type: section.type,
        dataTable: section?.dataTable,
        title: section.name,
        startYear: detailReport?.startYear,
        endYear: detailReport?.endYear,
        baseYear: detailReport?.baseYear,
        countryName: detailReport?.countryName
      });
    }
    await delay(1000);
    return pdf(<PDFDocument items={items} />).toBlob();
  };

  return (
    <Dialog
      PaperProps={{ style: { overflowY: 'visible' } }}
      maxWidth={'xl'}
      transitionDuration={0.3}
      open={isOpen}
      onClose={_onClose}
      className={classes.container}
    >
      <Box width={"100%"}>
        <Box className={classes.headerActionDialog}>
          <DialogTitle className={classes.title} textAlign={'center'}>Preview report</DialogTitle>
          <div onClick={_onClose} className={classes.butonCloseHeader}>
            <CloseIcon />
          </div>
        </Box>
      </Box>
      <DialogContent sx={{ overflowY: { md: 'visible' } }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: '10px' }} >
              <CardContent className={classes.content} sx={{ padding: '20px 0' }}>
                <div >{pageComponent}</div>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ mt: 10 }} className={classes.dialogAction}>
        <Box display={'flex'} gap={2} justifyContent={'space-between'} width={'100%'} sx={{ ml: 'auto' }}>
          <SecondaryButton disabled={!!(page <= 1)} type="button" onClick={prePage}>
            Previous page
          </SecondaryButton>
          <PrimaryButton onClick={exportPDF} type="button">
            Export
          </PrimaryButton>
          <SecondaryButton disabled={!!(page >= detailReport?.pages?.length)} form="form-download-table" onClick={nextPage}>
            Next page
          </SecondaryButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
});

export default ModalReport;
